import isEqual from "lodash.isequal";

import type {Result, ValidCacheData} from "@khanacademy/wonder-blocks-data";

import type {School} from "#auth/components/school-selector/types.ts";

// sync-start:school-picker-countries 1278984718 apps/khanacademy/src/auth/components/school-selector/school-selector.tsx
const COUNTRIES_SUPPORTED_BY_SCHOOL_PICKER = new Set([
    "US",
    "PE",
    "BR",
    "IN",
    "PH",
    "BD",
    "PK",
    // Note that including a country code here will cause that country
    // to _always_ be considered on by schoolPickerSupported().
    //
    // If you need to make a country conditional on a feature flag
    // add a special case in schoolPickerSupported().
]);
// sync-end:school-picker-countries

export function schoolPickerSupported(
    countryCode: string,
    vietnamSchoolPickerOn: boolean,
    kenyaSchoolPickerOn: boolean,
): boolean {
    // TODO(evanchaney): add VN to COUNTRIES_SUPPORTED_BY_SCHOOL_PICKER
    // once the Vietnam school picker is released.
    if (countryCode === "VN" && vietnamSchoolPickerOn) {
        return true;
    }

    // TODO(russell): add KE to COUNTRIES_SUPPORTED_BY_SCHOOL_PICKER
    // once the Kenya school picker is released.
    if (countryCode === "KE" && kenyaSchoolPickerOn) {
        return true;
    }

    return COUNTRIES_SUPPORTED_BY_SCHOOL_PICKER.has(countryCode);
}

export function canSaveCountryAndSchool(
    vietnamSchoolPickerOn: boolean,
    kenyaSchoolPickerOn: boolean,
    currentCountryCode?: string | null,
    currentSchool?: School | null,
    lastSavedCountryCode?: string | null,
    lastSavedSchool?: School | null,
): boolean {
    if (!currentCountryCode) {
        // require a non-null countryCode.
        return false;
    }

    const hasChange =
        currentCountryCode !== lastSavedCountryCode ||
        !isEqual(currentSchool, lastSavedSchool);

    if (
        schoolPickerSupported(
            currentCountryCode,
            vietnamSchoolPickerOn,
            kenyaSchoolPickerOn,
        )
    ) {
        // For countries that support the school picker, currentSchool
        // cannot be null, and there must be change.
        return !!currentSchool && hasChange;
    }

    // If the school picker is not supported by the selected country,
    // currentSchool can be null.
    return hasChange;
}

// Replace value (of some type T) within a promised result with a new
// promise of type Result<some type V>. Note that this only applies to the success case.
export async function mapPromise<
    T extends ValidCacheData,
    V extends ValidCacheData,
>(promisedResult: Promise<Result<T>>, value: V): Promise<Result<V>> {
    try {
        const result = await promisedResult;
        switch (result.status) {
            case "success":
                return {status: "success", data: value};
            case "no-data":
            case "loading":
                return {status: "loading"};
            case "error":
                return {status: "error", error: result.error};
            case "aborted":
                return {status: "aborted"};
        }
    } catch (e) {
        return {status: "error", error: e as Error};
    }
}
